import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchReports } from '../../api';
import { useAccess, PERMISSIONS } from '../auth/AccessContext';

// Loads the report catalog once and shares it with the Sidebar (to build the
// Reports submenu) and the ReportView (to look up the active report's metadata),
// so the list is fetched a single time per session.
const ReportsContext = createContext({ reports: [], loading: true, error: null });

export const useReports = () => useContext(ReportsContext);

export function ReportsProvider({ children }) {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // The catalog the server returns is already filtered to the reports this
    // caller's profile grants. Someone without the Reports section skips the
    // fetch entirely — it would only 403 and light up the sidebar with an error.
    const { loading: accessLoading, can } = useAccess();
    const canSeeReports = can(PERMISSIONS.reports);

    useEffect(() => {
        if (accessLoading) return undefined;
        if (!canSeeReports) {
            setReports([]);
            setLoading(false);
            return undefined;
        }
        let active = true;
        setLoading(true);
        fetchReports()
            .then(data => { if (active) setReports(data || []); })
            .catch(err => { if (active) setError(err.message); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [accessLoading, canSeeReports]);

    return (
        <ReportsContext.Provider value={{ reports, loading, error }}>
            {children}
        </ReportsContext.Provider>
    );
}
